import React, { useCallback } from "react";
import {
  Menu,
  MenuItem,
  Position,
  Spinner,
  Tag,
  Tooltip,
} from "@blueprintjs/core";
import "styled-components/macro";
import { useBoolean } from "../../helpers/useBoolean";
import { StyledPopoverWrapper } from "../../popover-wrapper";
import _ from "lodash";
import { Intent } from "@blueprintjs/core/lib/esm/common/intent";
import { useMeData } from "../../hooks/useMe";
import { UserRole } from "../../store/model-me";
import { ENROLLMENT_TAGS_INFO } from "../../entities/ecp/tags";
import { useStoreActions } from "../../hooks/ep";

const ALL_MENU_ITEMS = ["tag__behind_pace"];
// eslint-disable-next-line
const USER_ROLES__NO_ACCESS = [
  UserRole.ADMISSIONS_COORDINATOR,
  UserRole.DEFAULT,
  UserRole.ENROLLMENT_COORDINATOR,
  UserRole.ENROLLMENT_MANAGER,
];
const USER_ROLES__UNIVERSAL_ACCESS = [
  UserRole.SAXECAP_DEV,
  UserRole.ADMIN_READ_WRITE,
  UserRole.ADMIN_READ_ONLY,
  UserRole.ACADEMIC_DEPARTMENT_CHAIR,
  UserRole.DEAN_OF_FACULTY,
];

function useAuthorizedMenuItems() {
  const { role } = useMeData();
  if (USER_ROLES__UNIVERSAL_ACCESS.includes(role)) {
    return ALL_MENU_ITEMS;
  } else {
    return [];
  }
}

export function TeachercourseEmailActionButtonCellRenderer({
  data,
  value,
  crp,
}: {
  data: any;
  value: string;
  crp: { tooltipText?: string };
}) {
  const authorizedMenuItems = useAuthorizedMenuItems();
  const isLoading = useBoolean(false);
  const handleFetchMailtoLink = useStoreActions(
    (a) => a.misc.handleFetchMailtoLink
  );

  const teacherCourse_id = data["teacherCourse_id"];

  const enabledMenuItems = ALL_MENU_ITEMS.filter((tagField) => data[tagField]);
  const isMenuItemDisabled = (menuItemId) =>
    !authorizedMenuItems.includes(menuItemId) ||
    !enabledMenuItems.includes(menuItemId);
  const isPopoverTargetDisabled =
    _.size(enabledMenuItems) === 0 || isLoading.value;

  const onClickFactory = useCallback(
    (emailTemplateId) => async () => {
      if (isPopoverTargetDisabled) {
        return;
      }
      isLoading.setTrue();
      await handleFetchMailtoLink({
        entityType: "teacherCourse",
        entityId: teacherCourse_id,
        emailTemplateId,
      });
      isLoading.setFalse();
    },
    // eslint-disable-next-line
    [
      teacherCourse_id,
      isPopoverTargetDisabled,
      isLoading.setTrue,
      isLoading.setFalse,
    ]
  );

  const BehindPaceIcon = ENROLLMENT_TAGS_INFO.tagIcons.behind_pace;

  const popoverContentMenu = (
    <Menu large>
      <MenuItem
        intent={Intent.WARNING}
        icon={<BehindPaceIcon />}
        text={ENROLLMENT_TAGS_INFO.tagDisplayNames.behind_pace}
        onClick={onClickFactory("tag__behind_pace")}
        shouldDismissPopover={false}
        disabled={isMenuItemDisabled("tag__behind_pace")}
      />
    </Menu>
  );

  return (
    <span>
      <StyledPopoverWrapper
        canEscapeKeyClose
        content={popoverContentMenu}
        position={Position.RIGHT_BOTTOM}
        usePortal={false}
        hoverCloseDelay={0}
        isOpen={isLoading.value ? false : null}
      >
        <Tooltip
          content={"Compose email from template"}
          boundary="viewport"
          hoverOpenDelay={1500}
          hoverCloseDelay={0}
          isOpen={isLoading.value ? false : null}
        >
          <Tag
            css={`
              font-size: 12px;
              height: 26px;
              & .bp3-icon,
              & .bp3-spinner {
                margin-right: 6px;
              }
            `}
            fill={true}
            minimal={true}
            intent={isPopoverTargetDisabled ? "none" : "primary"}
            large={false}
            icon={
              isLoading.value ? (
                <Spinner size={Spinner.SIZE_SMALL} />
              ) : (
                "envelope"
              )
            }
            interactive={!isPopoverTargetDisabled}
            disabled={isPopoverTargetDisabled}
          />
        </Tooltip>
      </StyledPopoverWrapper>
    </span>
  );
}
